/**
 * Gen-1-style upload panel: stage many files, pick owner, upload with per-file status.
 */
import { useCallback, useRef, useState } from "react";
import { Upload, X, Check, AlertCircle, Loader2 } from "lucide-react";
import { useUploadTrack } from "../hooks/useTracks";

type FileStatus = "pending" | "uploading" | "done" | "error";

interface StagedFile {
  id: string;
  file: File;
  status: FileStatus;
  message?: string;
}

interface Props {
  owners: string[];
  defaultOwner?: string;
}

let nextId = 0;

export const LibraryUpload: React.FC<Props> = ({ owners, defaultOwner = "" }) => {
  const upload = useUploadTrack();
  const inputRef = useRef<HTMLInputElement>(null);
  const [owner, setOwner] = useState(defaultOwner);
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);

  const addFiles = useCallback((files: FileList | File[]) => {
    const list = Array.from(files).filter((f) => f.size > 0);
    if (list.length === 0) return;
    setStaged((prev) => [
      ...prev,
      ...list.map((file) => ({
        id: `f-${++nextId}`,
        file,
        status: "pending" as const,
      })),
    ]);
  }, []);

  const removeStaged = (id: string): void => {
    setStaged((prev) => prev.filter((s) => s.id !== id));
  };

  const clearDone = (): void => {
    setStaged((prev) => prev.filter((s) => s.status !== "done"));
  };

  const clearAll = (): void => {
    if (uploading) return;
    setStaged([]);
  };

  const runUpload = async (): Promise<void> => {
    const ownerTrim = owner.trim();
    if (!ownerTrim) {
      alert("Enter an owner name before uploading.");
      return;
    }
    const pending = staged.filter((s) => s.status === "pending" || s.status === "error");
    if (pending.length === 0) return;

    setUploading(true);
    for (const item of pending) {
      setStaged((prev) =>
        prev.map((s) =>
          s.id === item.id ? { ...s, status: "uploading", message: undefined } : s,
        ),
      );
      try {
        await upload.mutateAsync({ owner: ownerTrim, file: item.file });
        setStaged((prev) =>
          prev.map((s) =>
            s.id === item.id ? { ...s, status: "done", message: "Uploaded" } : s,
          ),
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Upload failed";
        setStaged((prev) =>
          prev.map((s) => (s.id === item.id ? { ...s, status: "error", message: msg } : s)),
        );
      }
    }
    setUploading(false);
  };

  const pendingCount = staged.filter(
    (s) => s.status === "pending" || s.status === "error",
  ).length;
  const doneCount = staged.filter((s) => s.status === "done").length;

  return (
    <section className="card p-4">
      <h2 className="mb-1 text-sm font-semibold">Upload tracks</h2>
      <p className="mb-3 text-xs text-gray-500">
        Stage multiple files, set the owner once, then upload in batch. Each file shows its own
        status.
      </p>

      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <label className="text-xs text-gray-500 sm:w-14">Owner</label>
        <input
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          list="library-upload-owners"
          placeholder="e.g. Josh"
          className="flex-1 rounded-xl border px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
          disabled={uploading}
        />
        <datalist id="library-upload-owners">
          {owners.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
        }}
        className={`rounded-2xl border-2 border-dashed p-6 text-center transition ${
          drag
            ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950"
            : "border-gray-300 dark:border-gray-700"
        }`}
      >
        <Upload className="mx-auto h-7 w-7 text-gray-400" />
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Drop audio files here or{" "}
          <button
            type="button"
            className="text-indigo-600 underline dark:text-indigo-400"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            browse
          </button>
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="audio/*"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {staged.length > 0 && (
        <div className="mt-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
            <span>
              {staged.length} file{staged.length !== 1 ? "s" : ""} staged
              {doneCount > 0 ? ` · ${doneCount} done` : ""}
            </span>
            <div className="flex gap-2">
              {doneCount > 0 && (
                <button type="button" className="btn text-xs" onClick={clearDone} disabled={uploading}>
                  Clear done
                </button>
              )}
              <button type="button" className="btn text-xs" onClick={clearAll} disabled={uploading}>
                Clear all
              </button>
            </div>
          </div>
          <ul className="max-h-56 divide-y overflow-y-auto rounded-xl border dark:divide-gray-800 dark:border-gray-800">
            {staged.map((item) => (
              <li
                key={item.id}
                className="flex items-center gap-2 px-3 py-2 text-sm"
              >
                <StatusIcon status={item.status} />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{item.file.name}</div>
                  <div className="text-xs text-gray-500">
                    {(item.file.size / 1024 / 1024).toFixed(2)} MB
                    {item.message ? ` · ${item.message}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn p-1"
                  aria-label={`Remove ${item.file.name}`}
                  disabled={item.status === "uploading"}
                  onClick={() => removeStaged(item.id)}
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="btn btn-primary mt-3 w-full sm:w-auto"
            disabled={uploading || pendingCount === 0 || !owner.trim()}
            onClick={() => void runUpload()}
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" /> Upload {pendingCount} file
                {pendingCount !== 1 ? "s" : ""}
              </>
            )}
          </button>
        </div>
      )}
    </section>
  );
};

const StatusIcon: React.FC<{ status: FileStatus }> = ({ status }) => {
  switch (status) {
    case "uploading":
      return <Loader2 className="h-4 w-4 shrink-0 animate-spin text-indigo-500" />;
    case "done":
      return <Check className="h-4 w-4 shrink-0 text-green-600" />;
    case "error":
      return <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />;
    default:
      return <span className="h-4 w-4 shrink-0 rounded-full border-2 border-gray-300" />;
  }
};
